import { Typography,Button, Card, Col, Flex, Row, Space, Table, Tag, Spin } from "antd";
import React, { useContext, useState } from "react";
import Title from "antd/es/typography/Title";
import { useEffect } from "react";
import { UnorderedListOutlined } from "@ant-design/icons";
import { DeleteIcon, EditIcon } from "../../../../../assets/svg";
import { colors } from "../../../../../constants/colors";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getServicesAction } from "../../../../../redux/services/services/servicesServices";
import { routes } from "../../../../../constants";
import { baseRouteMaker } from "../../../../../constants/routeConsts";
import Actions from "../../../../../redux/actions";
import ServiceDeleteDialog from "./ServiceDeleteDialog";
import Spinner from "../../../../common/spinner/Spinner";
import "./Services.scss";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import useDebounce from "../../../../../hooks/useDebounce";
import { getSystemSettingsAction } from "../../../../../redux/services/addon-and-sponsorship/addonAndSponsorshipServices";
import { LanguageContext } from "../../../../../context/LanguageProvider";

const ServicesList = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  const { selectedLanguage } = useContext(LanguageContext);

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { services } = useSelector((state) => state.services);
  const [searchValue] = useState("");
  const { debouncedValue: debouncedSearchValue } = useDebounce(
    searchValue,
    500
  );
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: services?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  const { systemSettings } = useSelector((state) => state.addonAndSponsorship);
  /* table dependencies */

  /* dialog dependencies */
  const [openServiceDeleteDialog, setOpenServiceDeleteDialog] = useState(false);
  /* dialog dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
    };
    dispatch(getServicesAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  useEffect(() => {
    dispatch(getSystemSettingsAction(setLoading));
  }, []);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("servicesList.title"),
        dataIndex: "title",
        key: "title",
      },
      {
        title: t("servicesList.category"),
        dataIndex: "category",
        render: (_, { categoryInfo }) => <>{selectedLanguage?.value === "ar" ? categoryInfo?.category?.locales[0]?.name : categoryInfo?.category?.name}</>,
      },
      {
        title: t("servicesList.price"),
        dataIndex: "price",
        key: "price",
        render: (_, { price }) => (
          <>{`${price} ${systemSettings?.system_currency}`}</>
        ),
      },
      {
        title: t("servicesList.availability"),
        key: "is_active",
        dataIndex: "is_active",
        render: (_, { is_active }) => (
          <Tag color={is_active ? "green" : "red"}>
            {is_active ? "AVAILABLE" : "NOT AVAILABLE"}
          </Tag>
        ),
      },
      {
        title: t("servicesList.day"),
        key: "availability",
        dataIndex: "availability",
        render: (_, { availability }) => (
          <div
            style={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "150px",
              overflow: "hidden",
            }}
          >
            {availability?.map((element) => element?.day).join(", ") || "-"}
          </div>
        ),
      },
      {
        title: t("servicesList.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              {record?.is_active ? (
                <Flex gap="small" justify="center">
                  <Space size="middle">
                    <Button
                      type="text"
                      size="medium"
                      icon={<EditIcon fillColor={colors.slateGray} />}
                      onClick={(e) => handleAddEditDetails(e, record)}
                    />
                  </Space>
                  <Space size="middle">
                    <Button
                      type="text"
                      size="medium"
                      icon={<DeleteIcon fillColor={colors.slateGray} />}
                      onClick={(e) => handleConfirmDelete(e, record)}
                    />
                  </Space>
                </Flex>
              ) : (
                <>-</>
              )}
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleAddEditDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_SERVICE_ID, {
        selectedServiceId: "",
      })
    );
    navigate(baseRouteMaker(routes.CREATE_SERVICE));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedServiceId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_SERVICE_ID, { selectedServiceId })
      );
    }
  };

  const handleConfirmDelete = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_SERVICE_ID, {
        selectedServiceId: "",
      })
    );
    setOpenServiceDeleteDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedServiceId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_SERVICE_ID, { selectedServiceId })
      );
    }
  };

  const isOrgVerified = useSelector(
    (state) => state?.auth?.loginDetails?.organization_id?.organization_verified
  );

  const openVerificationDialog = () => {
    dispatch(Actions.createAction(Actions.VERIFIED_DIALOG_OPEN));
  };

  return (
    <div className="servicesListContainer">
      <Flex vertical gap="2rem">
        <Flex align="center" justify="space-between">
        <Card
                  bordered
                  hoverable
                  style={{
                    width: 300,
                    padding: 0,
                    borderRadius: 20,
                    border: `1px solid ${colors.oceanBlue}`,
                  }}
                >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={6}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<UnorderedListOutlined style={{ fontSize: 22 }} />}
                    style={{ background: colors.oceanBlue }}
                  />
                </Col>
                <Col span={18}>
                  <Flex vertical>
                    <Typography.Title level={3}>
                      {t("servicesList.services")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.DarkMint }}
                    >
                      {`${services?.total || 0}`}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
            </Card>
          <Button
            type="primary"
            onClick={(e) =>
              isOrgVerified
                ? handleAddEditDetails(e, null)
                : openVerificationDialog()
            }
          >
            {t("servicesList.addService")}
          </Button>
        </Flex>
        {loading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={services?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
      <ServiceDeleteDialog
        open={openServiceDeleteDialog}
        onClose={() => {
          setOpenServiceDeleteDialog(false);
          handleGetTableData();
        }}
      />
    </div>
  );
};

export default ServicesList;
